import { useState } from "react";
import type { MouseEvent } from "react";
import { countTag, theme, type as text } from "../theme";
import { activeArea } from "./areas";
import type { LinkAdapter } from "./LinkAdapter";
import { isPlainLeftClick } from "./LinkAdapter";
import { badgeValue } from "./AppSidebar";
import type { NavCounts } from "./NavCountsContext";
import type { NavItem, NavModel } from "./types";

// ── Area tabs ─────────────────────────────────────────────────────────────
// The pages of the current task area, as one row of tabs above the page. The
// sidebar carries one row per area; this is where the area's pages live. A
// single-page area shows no tabs at all.

function Tab({ tab, current, adapter, counts }: { tab: NavItem; current: boolean; adapter: LinkAdapter; counts: NavCounts | null }) {
  const [hov, setHov] = useState(false);
  const to = tab.to ?? "/";
  const badge = badgeValue(counts, tab.badge);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    adapter.navigate(to);
  };
  return (
    <a
      href={adapter.href(to)}
      onClick={onClick}
      aria-current={current ? "page" : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      // An index tab: the current one is cut from the page below it (same ground, no bottom rule,
      // the marker along its top edge); the others sit behind, one step lower.
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: current ? "8px 14px 9px" : "6px 12px 7px",
        fontSize: text.body.fontSize, fontWeight: current ? 600 : 500, fontFamily: theme.font,
        color: current ? "var(--c-heading)" : hov ? "var(--c-text)" : "var(--c-muted)",
        background: current ? "var(--c-bg)" : hov ? "var(--c-panel)" : "var(--c-panelAlt)",
        border: `1px solid ${current ? "var(--c-line)" : "var(--c-border)"}`,
        borderBottom: current ? "none" : "1px solid var(--c-line)",
        borderRadius: "3px 3px 0 0",
        boxShadow: current ? "inset 0 3px 0 var(--c-mark)" : undefined,
        position: "relative", zIndex: current ? 1 : 0,
        textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0,
        transition: "color 0.11s, background 0.11s",
      }}
    >
      {tab.label}
      {typeof badge === "number" && (
        <span style={countTag}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </a>
  );
}

export function AreaTabs({ model, adapter, counts, isMobile }: { model: NavModel; adapter: LinkAdapter; counts: NavCounts | null; isMobile: boolean }) {
  const found = activeArea(model, adapter);
  if (!found) return null;
  const tabs = found.area.tabs ?? [];
  if (tabs.length < 2) return null;
  return (
    <nav
      aria-label={`${found.area.label} pages`}
      style={{
        display: "flex", alignItems: "flex-end", gap: 3, overflowX: "auto", overflowY: "hidden",
        padding: isMobile ? "6px 12px 0" : "8px 24px 0",
        // The baseline is an inset shadow, not a border: the strip clips overflow, so a border
        // could not be covered, while the current tab's own ground paints over this line.
        boxShadow: "inset 0 -1px 0 var(--c-line)",
        background: "var(--c-panel)",
        flexShrink: 0,
        scrollbarWidth: "thin",
      }}
    >
      {tabs.map((tab) => (
        <Tab key={tab.id} tab={tab} current={tab.id === found.tab.id} adapter={adapter} counts={counts} />
      ))}
    </nav>
  );
}
